import { useEffect, useRef, useState } from "react";
import { Column, Row, Spacer } from "../../components/Layout/Layout";
import { usePageFilter } from "../../core/hooks/filters/usePageFilter";
import { useQueryState } from "../../core/hooks/filters/useQueryState";
import { useSortParams } from "../../core/hooks/filters/useSortParams";
import {
  storeTableState,
  useStoredTableState,
} from "../../core/hooks/filters/useStoredTableState";
import useTranslations from "../../core/i18n/useTranslations";
import Button from "../../ui-lib/components/Button/Button";
import { SearchInput } from "../../ui-lib/components/Inputs/SearchInput";
import {
  searchStringParser,
  urlSearchParser,
} from "../../ui-lib/utils/urlSearchParser";
import LoadingSpinner from "../../ui-lib/components/Loading/LoadingSpinner";
import {
  IObservationIdentifier,
  ObservationIdentifiersSearchTypes,
} from "../../core/api/observationIdentifiers/types";
import TableRowSelectionPopup, {
  TableRowSelectionPopupActions,
} from "../../ui-lib/components/Tables/TableRowSelectionPopup";
import ObservationIdentifiersTable from "../../components/ObservationIdentifiers/ObservationIdentifiersTable";
import {
  deleteKLObservationIdentifier,
  getObservationIdentifiers,
} from "../../core/api/observationIdentifiers/identifiers";
import {
  notifyApiErrors,
  sortParamsToString,
} from "../../core/helpers/helpers";
import useUser from "../../core/user/useUser";
import useSharedLocation from "../../pages/Locations/SharedLocationContext/useSharedLocationContext";
import BulkDeleteModal from "../../components/BulkDeleteModal";
import tableSelectionModule from "../../ui-lib/components/Tables/TableRowSelectionPopup.module.css";
import EditObservationIdentifierModal from "../../components/ObservationIdentifiers/EditObservationIdentifierModal";
import CreateObservationIdentifierModal from "../../components/ObservationIdentifiers/CreateObservationIdentifierModal";
import { observer } from "mobx-react";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";

const ObservationIdentifiersPage = ({
  pageId,
  tableClassName,
  children,
}: {
  pageId: string;
  tableClassName?: string;
  children?: (onCreate: () => void) => React.ReactNode;
}) => {
  const t = useTranslations();
  const user = useUser();
  const { location, fetchLocation } = useSharedLocation();
  const isExtended = !location;
  storeTableState(pageId);
  const tableState = useStoredTableState(pageId);
  const searchState = urlSearchParser(tableState) ?? "";
  const searchFilter =
    searchStringParser(searchState.s) ??
    ObservationIdentifiersSearchTypes.Identifier;

  const [queryParams, setQueryParams] = useQueryState<{ q: string; s: string }>(
    {
      q: searchStringParser(searchState.q) ?? "",
      s: isExtended ? searchFilter : "",
    },
  );
  const searchValue = queryParams.q ?? "";
  const searchPlaceholder = queryParams.s
    ? t(`Locations:oi_search_placeholder_${queryParams.s?.toLocaleLowerCase()}`)
    : t("Locations:oi_search_by_mac_placeholder");
  const { pageFilter, setPageFilter, resetPageFilter } =
    usePageFilter(searchState);
  const { sortParams, setSortParams } = useSortParams(searchState);
  const clearSelectedFunction = useRef<Function>();
  const tableRowSelectionPopupRef =
    useRef<TableRowSelectionPopupActions<Record<string, any>>>(null);
  const selectedItemRows = useRef<IObservationIdentifier[]>([]);
  const locationId =
    location?.id || selectedItemRows.current[0]?.knownLocationId;
  const [data, setData] = useState<IObservationIdentifier[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { dataDefinitions, total } = await getObservationIdentifiers(
        {
          page: pageFilter.page,
          pageSize: pageFilter.pageSize,
          q: queryParams.q,
          o: sortParamsToString(sortParams, true),
          s: queryParams.s,
        },
        user.authenticatedRequest,
        location?.id,
      );
      setTotalItems(total);
      setData(dataDefinitions);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
      clearSelectedFunction.current?.();
      tableRowSelectionPopupRef.current?.close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageFilter, searchValue, sortParams]);

  useEffect(() => {
    clearSelectedFunction.current?.();
    tableRowSelectionPopupRef.current?.close(true);
  }, [data]);

  const searchFields = Object.values(ObservationIdentifiersSearchTypes).map(
    (filter) => ({
      id: filter,
      name: t(`Locations:oi_search_label_${filter.toLocaleLowerCase()}`),
      isSelected: queryParams.s === filter,
    }),
  );

  return (
    <Column className="w-100">
      <CreateObservationIdentifierModal
        isOpen={isCreateModalOpen}
        locationId={location?.id}
        onUpdate={() => {
          fetchData();
          setIsCreateModalOpen(false);
          fetchLocation();
        }}
        onClose={() => {
          setIsCreateModalOpen(false);
        }}
      />
      <EditObservationIdentifierModal
        isOpen={isEditModalOpen}
        locationId={locationId}
        observationIdentifier={selectedItemRows.current[0]}
        onUpdate={() => {
          fetchData();
          setIsEditModalOpen(false);
        }}
        onClose={() => {
          setIsEditModalOpen(false);
        }}
      />
      <BulkDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={() => {
          setIsDeleteModalOpen(false);
          clearSelectedFunction.current?.();
          tableRowSelectionPopupRef.current?.close(true);
          resetPageFilter();
          fetchLocation();
        }}
        ids={selectedItemRows.current?.map((s) => s.id.toString()) || []}
        labels={{
          single: t("Locations:oi_type_single"),
          multi: t("Locations:oi_type_multi"),
        }}
        apiFunc={deleteKLObservationIdentifier}
      />

      <TableRowSelectionPopup
        ref={tableRowSelectionPopupRef}
        renderBody={(selectedItems) => (
          <div className={tableSelectionModule.bodyRow}>
            <Button
              variant="destructive"
              text={`${t("Common:delete_selection")} (${selectedItems.length})`}
              onClick={() => {
                selectedItemRows.current =
                  selectedItems as IObservationIdentifier[];
                setIsDeleteModalOpen(true);
              }}
            />
            <Button
              variant="secondary"
              text={t("Common:cancel")}
              onClick={() => {
                clearSelectedFunction.current?.();
                tableRowSelectionPopupRef.current?.close(true);
              }}
            />
          </div>
        )}
      />
      {children?.(() => setIsCreateModalOpen(true))}
      <Column className={tableClassName} align="fill" type="top">
        <Spacer size={8} />
        <Row align="start" type="left" className="flex0 w-100">
          {isExtended && (
            <>
              <Dropdown
                items={searchFields}
                onSelectItem={(filter) => {
                  setQueryParams((prev) => ({
                    ...prev,
                    s: filter.id.toString(),
                  }));
                }}
                selectedItem={searchFields.find((f) => f.isSelected)}
                width={205}
              />
              <Spacer size={8} />
            </>
          )}
          <SearchInput
            placeholder={searchPlaceholder}
            onChange={(val) => {
              if (searchValue == val) return;

              resetPageFilter();
              setQueryParams((prev) => ({ ...prev, q: val }));
            }}
            limit={1}
            value={queryParams.q}
          />
        </Row>
        {isLoading ? (
          <LoadingSpinner theme="primary" />
        ) : (
          <ObservationIdentifiersTable
            data={data}
            clearSelectedFunction={clearSelectedFunction}
            selectedItemRows={selectedItemRows}
            tableRowSelectionPopupRef={tableRowSelectionPopupRef}
            searchState={searchState}
            totalItems={totalItems}
            pageSettings={pageFilter}
            sortParams={sortParams}
            setIsDeleteModalOpen={setIsDeleteModalOpen}
            setIsEditModalOpen={setIsEditModalOpen}
            onPageSettingsChange={setPageFilter}
            setSortParams={setSortParams}
            resetPageSettings={resetPageFilter}
            extended={isExtended}
          />
        )}
      </Column>
    </Column>
  );
};

export default observer(ObservationIdentifiersPage);
